import {computed, inject, Injectable} from '@angular/core';
import {injectMutation, injectQuery, QueryClient} from '@tanstack/angular-query-experimental';
import {lastValueFrom, map} from 'rxjs';
import {
  AccountingExportService,
  CreditNoteService,
  CreditNoteStateId,
  downloadFile,
  extractFilenameFromContentDisposition,
  InvoiceService,
  InvoiceStateId,
} from '../../../../api';
import {QueryKey} from '../../../constants/query-key';
import {FinancesPeriodStore} from './finances-period.store';

export type AccountingExportRowType = 'INVOICE' | 'CREDIT_NOTE';

export interface AccountingExportRow {
  id: number;
  type: AccountingExportRowType;
  number: string | null;
  customerName: string;
  legalEntityName: string;
  date: Date;
  amountWithoutTax: number;
  accountingExportedAt?: Date | null;
}

@Injectable()
export class AccountingExportStore {
  private invoiceService = inject(InvoiceService);
  private creditNoteService = inject(CreditNoteService);
  private accountingExportService = inject(AccountingExportService);
  private periodStore = inject(FinancesPeriodStore);
  private queryClient = inject(QueryClient);

  // Drafts cannot be exported to accounting, so only issued documents are fetched.
  private readonly _exportableInvoiceStates = [InvoiceStateId.SENT, InvoiceStateId.PAID, InvoiceStateId.CREDITED];
  private readonly _exportableCreditNoteStates = [CreditNoteStateId.SENT];

  private get _hasDates() {
    const {startDate, endDate} = this.periodStore.period();
    return startDate != null && endDate != null;
  }

  private invoicesQuery = injectQuery(() => ({
    queryKey: [QueryKey.INVOICES, this.periodStore.period().startDate, this.periodStore.period().endDate, this._exportableInvoiceStates],
    queryFn: () => lastValueFrom(this.invoiceService.getInvoices(
      this.periodStore.period().startDate,
      this.periodStore.period().endDate,
      [], this._exportableInvoiceStates, [], [], null,
    )),
    enabled: this._hasDates,
  }));

  private creditNotesQuery = injectQuery(() => ({
    queryKey: [QueryKey.CREDIT_NOTES, this.periodStore.period().startDate, this.periodStore.period().endDate, this._exportableCreditNoteStates],
    queryFn: () => lastValueFrom(this.creditNoteService.getCreditNotes(
      this.periodStore.period().startDate,
      this.periodStore.period().endDate,
      [], this._exportableCreditNoteStates, [], null,
    )),
    enabled: this._hasDates,
  }));

  isLoading = computed(() => this.invoicesQuery.isLoading() || this.creditNotesQuery.isLoading());

  rows = computed<AccountingExportRow[]>(() => {
    const invoiceRows = (this.invoicesQuery.data()?.invoices ?? []).map<AccountingExportRow>(invoice => ({
      id: invoice.id,
      type: 'INVOICE',
      number: invoice.number,
      customerName: invoice.customerName,
      legalEntityName: invoice.legalEntityName,
      date: invoice.date,
      amountWithoutTax: invoice.amountWithoutTax,
      accountingExportedAt: invoice.accountingExportedAt,
    }));
    const creditNoteRows = (this.creditNotesQuery.data()?.creditNotes ?? []).map<AccountingExportRow>(creditNote => ({
      id: creditNote.id,
      type: 'CREDIT_NOTE',
      number: creditNote.number ?? null,
      customerName: creditNote.customerName,
      legalEntityName: creditNote.legalEntityName,
      date: creditNote.date,
      amountWithoutTax: creditNote.amountWithoutTax,
      accountingExportedAt: creditNote.accountingExportedAt,
    }));
    return [...invoiceRows, ...creditNoteRows];
  });

  private invoiceIds = computed(() => this.rows().filter(row => row.type === 'INVOICE').map(row => row.id));
  private creditNoteIds = computed(() => this.rows().filter(row => row.type === 'CREDIT_NOTE').map(row => row.id));

  previewMutation = injectMutation(() => ({
    mutationFn: () => lastValueFrom(this.accountingExportService.previewAccountingExport(
      {invoiceIds: this.invoiceIds(), creditNoteIds: this.creditNoteIds()},
      'response',
    ).pipe(map(response => {
      const filename = extractFilenameFromContentDisposition(
        response.headers.get('Content-Disposition'),
        'apercu-comptable.xlsx',
      );
      downloadFile(response.body!, filename);
    }))),
  }));

  exportMutation = injectMutation(() => ({
    mutationFn: () => lastValueFrom(this.accountingExportService.exportAccounting(
      {invoiceIds: this.invoiceIds(), creditNoteIds: this.creditNoteIds()},
      'response',
    ).pipe(map(response => {
      const filename = extractFilenameFromContentDisposition(
        response.headers.get('Content-Disposition'),
        'export-comptable',
      );
      downloadFile(response.body!, filename);
    }))),
    onSuccess: () => {
      // The accounting export stamps the exported date on invoices and credit notes; refresh so the grid shows it.
      this.queryClient.invalidateQueries({queryKey: [QueryKey.INVOICES]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.CREDIT_NOTES]});
    },
  }));
}
